import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {AlertService} from '../services/alert.service';
import {EnvService} from '../services/env.service';
import { AuthService } from '../services/auth.service';
import { forkJoin, of, Subscription } from 'rxjs';

@Component({
  selector: 'micro-cmdb-database-detail',
  templateUrl: './cmdb-database-detail.component.html'
})
export class CmdbDatabaseDetailComponent implements OnInit {
  @Input() cmdbDatabaseId: any;
  @Output() persisted = new EventEmitter<any>();
  cmdbDatabase: any;
  selectedApplication: any;
  validatingCompany = false;
  companyValidationFailed = false;
  private companyValidation?: Subscription;
  private subscriptions = new Subscription();

  constructor(public auth: AuthService, private env: EnvService, private http: HttpClient, private route: ActivatedRoute,
              private router: Router, private alertService: AlertService) {
  }

  ngOnInit(): void {
    if (this.cmdbDatabaseId) {
      this.load(this.cmdbDatabaseId);

    } else {
      this.subscriptions.add(this.route.params.subscribe((params: Params) => {
        this.selectedApplication = undefined;
        params['id'] === '0' ? this.createNew() : this.load(params['id']);
      }));
    }
  }

  load(id: string): void {
    this.companyValidation?.unsubscribe();
    this.selectedApplication = undefined;
    this.subscriptions.add(this.http.get(`${this.env.e.url}/domain/cmdbDatabases/${id}`).subscribe(data => {
      this.cmdbDatabase = data;
      this.validateCompanySelections();
    }));
  }

  createNew(): void {
    this.companyValidation?.unsubscribe();
    this.selectedApplication = undefined;
    this.validatingCompany = false;
    this.companyValidationFailed = false;
    this.cmdbDatabase = { name: '', applicationId: '', environmentId: '', sqlServerTypeId: '', description: 'None', active: 'N', companyId: this.auth.getCompanyId() };
  }

  save(): void {
    if (this.validatingCompany || this.companyValidationFailed || !this.cmdbDatabase.companyId) {
      return;
    }
    const params = new HttpParams().set('companyId', this.cmdbDatabase.companyId || this.auth.getCompanyId());
    const isUpdate = !!this.cmdbDatabase.id;
    const request = isUpdate
      ? this.http.put(`${this.env.e.url}/domain/cmdbDatabases/${this.cmdbDatabase.id}`, this.cmdbDatabase, {params})
      : this.http.post(`${this.env.e.url}/domain/cmdbDatabases`, this.cmdbDatabase, {params});
    this.subscriptions.add(request.subscribe(data => {
      this.cmdbDatabase = data;
      this.alertService.info(`${isUpdate ? 'Updated' : 'Created'} ${this.cmdbDatabase.name}`);
      this.persisted.emit({data, action: isUpdate ? 'updated' : 'added'});
    }));
  }

  delete(): void {
    this.subscriptions.add(this.http.delete(`${this.env.e.url}/domain/cmdbDatabases/${this.cmdbDatabase.id}`).subscribe(() => {
      this.persisted.emit({data: this.cmdbDatabase, action: 'deleted'});
      this.alertService.info(`Deleted ${this.cmdbDatabase.name}`);
      if (!this.cmdbDatabaseId) { this.router.navigate(['/cmdbDatabases']); }
    }));
  }

  ngOnDestroy(): void {
    this.companyValidation?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onCompanyChange(companyId: any): void {
    if (companyId === this.cmdbDatabase.companyId) {
      return;
    }
    this.cmdbDatabase.companyId = companyId;
    this.validateCompanySelections();
  }

  validateCompanySelections(): void {
    this.companyValidation?.unsubscribe();
    this.companyValidationFailed = false;
    const database = this.cmdbDatabase;
    const companyId = database.companyId;
    this.validatingCompany = !!companyId;
    if (!companyId) {
      return;
    }

    // Fetch the selected records themselves: absence from a dropdown page does
    // not establish that a selection belongs to a different company.
    this.companyValidation = forkJoin({
      application: database.applicationId
        ? this.http.get<any>(`${this.env.e.url}/domain/cmdbApplications/${database.applicationId}`)
        : of(null),
      environment: database.environmentId
        ? this.http.get<any>(`${this.env.e.url}/domain/environments/${database.environmentId}`)
        : of(null)
    }).subscribe({
      next: ({application, environment}) => {
        if (this.cmdbDatabase !== database || database.companyId !== companyId) {
          return;
        }
        if (application && application.companyId !== companyId) {
          database.applicationId = '';
        }
        if (environment && environment.companyId !== companyId) {
          database.environmentId = '';
        }
        this.selectedApplication = database.applicationId ? application : undefined;
        // Missing records cannot be validated. Preserve their IDs and block save.
        this.companyValidationFailed = !!((database.applicationId && !application)
          || (database.environmentId && !environment));
        this.validatingCompany = false;
      },
      error: () => {
        this.validatingCompany = false;
        this.companyValidationFailed = true;
        this.alertService.warn('Unable to validate the selected records for this company. Please retry.');
      }
    });
  }

  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.cmdbDatabase.active = target.checked ? 'Y' : 'N';
  }
}
